import { useEffect } from 'react';
import { Mail, Phone, MapPin, Clock, Send, MessageSquare, Users, Globe } from 'lucide-react';

const Contact = () => {
  useEffect(() => {
    document.title = 'Contact Us - FemSportNet';
  }, []);

  const contactMethods = [
    {
      icon: <Mail className="h-6 w-6 text-green-500" />,
      title: 'Email Support',
      description: 'Get help with your account or technical issues',
      contact: 'femsportnet@femsportnet.com',
      response: 'Within 24 hours'
    },
    {
      icon: <Phone className="h-6 w-6 text-green-500" />,
      title: 'Phone Support',
      description: 'Speak directly with our support team',
      contact: 'femsportnet@femsportnet.com',
      response: 'Mon-Fri, 9AM-6PM PST'
    },
    {
      icon: <MessageSquare className="h-6 w-6 text-green-500" />,
      title: 'Live Chat',
      description: 'Chat with us in real-time',
      contact: 'Available on platform',
      response: 'Mon-Fri, 9AM-6PM PST'
    },
    {
      icon: <Users className="h-6 w-6 text-green-500" />,
      title: 'Community Forum',
      description: 'Get help from other athletes',
      contact: 'femsportnet@femsportnet.com',
      response: 'Community moderated'
    }
  ];

  const offices = [
    {
      city: 'San Francisco',
      address: '123 Tech Street, Suite 400',
      zipCode: 'San Francisco, CA 94105',
      phone: '+1 (555) 123-4567',
      email: 'femsportnetredsocial@gmail.com'
    },
    {
      city: 'New York',
      address: '456 Sports Avenue, Floor 12',
      zipCode: 'New York, NY 10001',
      phone: '+1 (555) 123-4568',
      email: 'femsportnetredsocial@gmail.com'
    },
    {
      city: 'London',
      address: '789 Athletic Road, Suite 200',
      zipCode: 'London, UK SW1A 1AA',
      phone: '+44 20 7123 4567',
      email: 'femsportnetredsocial@gmail.com'
    }
  ];

  const supportChannels = [
    {
      title: 'General Support',
      email: 'femsportnetredsocial@gmail.com',
      description: 'Account issues, technical problems, general questions'
    },
    {
      title: 'Partnership Inquiries',
      email: 'femsportnetredsocial@gmail.com',
      description: 'Brand partnerships, sponsorships, collaboration opportunities'
    },
    {
      title: 'Press & Media',
      email: 'femsportnetredsocial@gmail.com',
      description: 'Media inquiries, press releases, interview requests'
    },
    {
      title: 'Business Development',
      email: 'femsportnetredsocial@gmail.com',
      description: 'Enterprise solutions, team accounts, custom integrations'
    }
  ];

  return (
    <div className="bg-white dark:bg-gray-900 min-h-screen transition-colors duration-200">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-green-600 to-green-800 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Get in Touch
            </h1>
            <p className="text-xl text-green-100 mb-8">
              We're here to help you succeed. Reach out to our team for support, partnerships, 
              or any questions about FemSportNet.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                How Can We Help?
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300">
                Choose the best way to reach us based on your needs
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {contactMethods.map((method, index) => (
                <div key={index} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 text-center hover:shadow-lg transition-all duration-200">
                  <div className="inline-flex items-center justify-center rounded-full bg-green-100 dark:bg-green-900 p-4 mb-4">
                    {method.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {method.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm mb-3">
                    {method.description}
                  </p>
                  <p className="font-medium text-gray-900 dark:text-white mb-1">
                    {method.contact}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {method.response}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-16 bg-gray-50 dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                Send Us a Message
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300">
                Fill out the form below and we'll get back to you as soon as possible
              </p>
            </div>
            
            <div className="bg-white dark:bg-gray-700 rounded-lg shadow-lg p-8">
              <form className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      required
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Your first name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      required
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Your last name"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    required
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Subject *
                  </label>
                  <select
                    required
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="">Select a subject</option>
                    <option value="support">Technical Support</option>
                    <option value="partnership">Partnership Inquiry</option>
                    <option value="press">Press & Media</option>
                    <option value="feedback">Feedback & Suggestions</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Message *
                  </label>
                  <textarea
                    required
                    rows={6}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Tell us how we can help you..."
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="newsletter"
                    className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                  />
                  <label htmlFor="newsletter" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    I'd like to receive updates about FemSportNet news and features
                  </label>
                </div>

                <div>
                  <button
                    type="submit"
                    className="w-full flex items-center justify-center px-6 py-3 bg-green-500 text-white font-medium rounded-lg hover:bg-green-600 transition-colors duration-200"
                  >
                    <Send size={18} className="mr-2" />
                    Send Message
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Support Channels */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                Specialized Support
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300">
                For specific inquiries, reach out to our specialized teams
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {supportChannels.map((channel, index) => (
                <div key={index} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {channel.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm mb-3">
                    {channel.description}
                  </p>
                  <a 
                    href={`mailto:${channel.email}`}
                    className="text-green-500 hover:text-green-600 font-medium"
                  >
                    {channel.email}
                  </a>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Office Locations */}
      <section className="py-16 bg-gray-50 dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                Our Offices
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300">
                Visit us at one of our global locations
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {offices.map((office, index) => (
                <div key={index} className="bg-white dark:bg-gray-700 rounded-lg shadow p-6">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    {office.city}
                  </h3>
                  
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <MapPin className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                      <div>
                        <p className="text-gray-600 dark:text-gray-300">{office.address}</p>
                        <p className="text-gray-600 dark:text-gray-300">{office.zipCode}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center">
                      <Phone className="h-5 w-5 text-gray-400 mr-2" />
                      <a 
                        href={`tel:${office.phone}`}
                        className="text-gray-600 dark:text-gray-300 hover:text-green-500"
                      >
                        {office.phone}
                      </a>
                    </div>
                    
                    <div className="flex items-center">
                      <Mail className="h-5 w-5 text-gray-400 mr-2" />
                      <a 
                        href={`mailto:${office.email}`}
                        className="text-gray-600 dark:text-gray-300 hover:text-green-500"
                      >
                        {office.email}
                      </a>
                    </div>
                    
                    <div className="flex items-center">
                      <Clock className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-gray-600 dark:text-gray-300">
                        Mon-Fri, 9AM-6PM
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300">
                Quick answers to common questions
              </p>
            </div>
            
            <div className="space-y-6">
              {[
                {
                  question: 'How do I create an account?',
                  answer: 'Click the "Sign Up" button in the top right corner and fill out the registration form. You\'ll need to provide your name, email, and choose your role (athlete, coach, etc.).'
                },
                {
                  question: 'Is FemSportNet free to use?',
                  answer: 'Yes! Basic features are completely free. We also offer premium plans with additional features for serious athletes and teams.'
                },
                {
                  question: 'How do I get verified?',
                  answer: 'Submit verification documents through your profile settings. Our team reviews applications within 48 hours.'
                },
                {
                  question: 'Can I sell products on the marketplace?',
                  answer: 'Yes! Any verified user can list sports equipment and services on our marketplace. We charge a small commission on successful sales.'
                },
                {
                  question: 'How do I report inappropriate content?',
                  answer: 'Use the report button on any post or profile. Our moderation team reviews all reports within 24 hours.'
                },
                {
                  question: 'Can I delete my account?',
                  answer: 'Yes, you can delete your account at any time from your privacy settings. This action is permanent and cannot be undone.'
                }
              ].map((faq, index) => (
                <div key={index} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {faq.question}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Emergency Contact */}
      <section className="py-16 bg-red-50 dark:bg-red-900/10">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl font-bold text-red-800 dark:text-red-200 mb-4">
              Emergency Support
            </h2>
            <p className="text-red-700 dark:text-red-300 mb-6">
              For urgent safety concerns or platform security issues
            </p>
            <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-6">
              <a 
                href="mailto:emergency@femsportnet.com"
                className="flex items-center px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200"
              >
                <Mail size={18} className="mr-2" />
                femsportnet@femsportnet.com
              </a>
              <a 
                href="tel:+15551234911"
                className="flex items-center px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200"
              >
                <Phone size={18} className="mr-2" />
                +1 (555) 123-4911
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;